import React, { CSSProperties, ReactNode } from 'react';
import styles from '../TargetGroupSettings.module.css';

interface SettingRowProps {
    label: ReactNode;
    description?: ReactNode;
    children: ReactNode;
    rowStyle?: CSSProperties;
}

/**
 * Generic key/value row used throughout the TargetGroupSettings tabs.
 * The left column is a label + optional description; the right column
 * is whatever the caller passes as `children` (Switch, TextInput, link, etc.).
 *
 * Replaces the repeated `<tr><td><div><span>{label}</span><p>{desc}</p></div></td><td>{value}</td></tr>`
 * pattern that appeared 10+ times in the original component.
 *
 * Description handling:
 *   - Plain string → auto-wrapped in <p> (matches the most common case where the
 *     CSS module's `p` selector styles the description with the right margin/color).
 *   - JSX (fragment, element, etc.) → rendered as-is, so callers can pass
 *     multi-paragraph content like `<><p>...</p><p>...</p></>` or a single `<p>`
 *     containing inline `<br/>` / `<i>` elements. This preserves the exact HTML
 *     structure of the original component.
 */
export const SettingRow = ({ label, description, children, rowStyle }: SettingRowProps) => (
    <tr style={rowStyle}>
        <td>
            <div>
                <span className={styles.target_group_settings__pane__configuration__config_label}>
                    {label}
                </span>
                {description !== undefined && description !== null
                    ? typeof description === 'string'
                        ? <p>{description}</p>
                        : description
                    : null}
            </div>
        </td>
        <td>{children}</td>
    </tr>
);

export default SettingRow;
